// pages/Portfolio/portfolioModel.js
//
// Pure helpers behind the redesigned Portfolio page (Portfolio.dc.html /
// PhonePortfolio.dc.html). Everything here is data-in, data-out so the page
// components stay thin and every rule the old PortfolioOverview.jsx encoded
// (filters drive the whole page, same asset merged across accounts, dust and
// small-balance collapse, top-5 + "Others (N)", "<1%" labels, pill overflow)
// is pinned by portfolioModel.test.js.
//
// Data honesty: a value the backend does not give us is `null`, and the UI
// renders "—" for it. Nothing here invents a number.

import { futuresBlockOf, getBotStatusMeta } from '../../components/Bot/botStatus';

// ── Accounts ────────────────────────────────────────────────────────────────

/**
 * 'sim' | 'paper' | 'live'. `simulated` is checked FIRST: the summary
 * defaults `accountType` to 'paper' for simulated rows
 * (crypto-backend-api services/portfolioValuation.js), so testing paper first
 * would label every demo account PAPER. An Alpaca paper account is
 * simulated:false (real upstream API) but is not live money.
 */
export function accountKind(ex) {
  if (!ex) return 'live';
  if (ex.simulated) return 'sim';
  return String(ex.accountType || '').toLowerCase() === 'paper' ? 'paper' : 'live';
}

export const KIND_LABEL = { sim: 'Simulated', paper: 'Paper', live: 'Live' };
export const KIND_TAG = { sim: 'SIM', paper: 'PAPER', live: 'LIVE' };

/** "Test Account · #7" / "Account · #6" — the row subtitle kept from today (E8). */
export const accountSubtitle = (ex) => `${ex && ex.accountType === 'paper' ? 'Test Account' : 'Account'} #${ex ? ex.id : ''}`;

/** NEEDS ASSETS / NEEDS LOCK badge for a simulated account (E7), or null. */
export function accountBadge(ex) {
  if (!ex || !ex.simulated) return null;
  const n = (ex.holdings || []).length;
  if (n === 0) return { key: 'assets', label: 'NEEDS ASSETS', title: 'This simulated account has no holdings yet' };
  if (!ex.assetsLocked) return { key: 'lock', label: 'NEEDS LOCK', title: 'Lock holdings to set the cost-basis baseline and track P&L' };
  return null;
}

// Short words for the summary's `balanceErrorCode` (crypto-backend-api
// services/exchangeBalancePoller.js classifyFailure). The full sentence is
// `balanceError`, shown as the tooltip.
const BALANCE_ERROR_SHORT = {
  geo_blocked: 'Blocked in this region',
  invalid_key: 'API key rejected',
  timestamp: 'Clock out of sync',
  network: 'Exchange unreachable',
  rate_limited: 'Rate-limited',
  no_credentials: 'No API key stored',
};

function agoLabel(iso, now) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return null;
  const mins = Math.floor((now - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(t).toLocaleDateString();
}

/**
 * Where a live account's balance came from, for the row subtitle. Generic
 * over every poller-snapshot source (Alpaca, Binance, MEXC):
 *   { error: { short, full } | null, note: string | null }
 * or null when the summary carries none of the fields (simulated accounts).
 */
export function balanceStatus(ex, now = Date.now()) {
  if (!ex || (!ex.balanceSource && !ex.balanceError && !ex.balancePending)) return null;
  const error = ex.balanceError
    ? { short: BALANCE_ERROR_SHORT[ex.balanceErrorCode] || 'Balance read failed', full: String(ex.balanceError) }
    : null;
  const parts = [];
  if (ex.balancePending) parts.push('Waiting for the first balance read');
  else {
    const ago = agoLabel(ex.balanceAsOf, now);
    if (ago) parts.push(`Balances ${ago}`);
  }
  const n = Number(ex.unpricedCount) || 0;
  if (n > 0) parts.push(`${n} unpriced`);
  return { error, note: parts.length ? parts.join(' · ') : null };
}

/** { total, live, paper, sim } */
export function countKinds(exchanges = []) {
  const out = { total: exchanges.length, live: 0, paper: 0, sim: 0 };
  exchanges.forEach((e) => { out[accountKind(e)] += 1; });
  return out;
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * The header's sub line: "3 accounts, 2 paper and 1 simulated." (mockup),
 * or for a scoped account "Alpaca_demo · Alpaca · Simulated." (P8).
 */
export function accountsLine(exchanges = [], scopedEx = null) {
  if (scopedEx) return `${scopedEx.name} · ${scopedEx.label || scopedEx.exchange || 'Exchange'} · ${KIND_LABEL[accountKind(scopedEx)]}.`;
  const c = countKinds(exchanges);
  if (c.total === 0) return 'No accounts in view.';
  const parts = [];
  if (c.live) parts.push(`${c.live} live`);
  if (c.paper) parts.push(`${c.paper} paper`);
  if (c.sim) parts.push(`${c.sim} simulated`);
  const head = plural(c.total, 'account');
  if (parts.length <= 1) return `${head}, ${parts[0] || ''}.`.replace(', .', '.');
  const last = parts.pop();
  return `${head}, ${parts.join(', ')} and ${last}.`;
}

/** "Updated just now" / "Updated 4 min ago" / "Updated at 14:02" from summary.asOf. */
export function updatedLabel(asOf, now = Date.now()) {
  const t = Number(asOf);
  if (!Number.isFinite(t) || t <= 0) return '';
  const mins = Math.floor((now - t) / 60000);
  if (mins < 1) return 'Updated just now.';
  if (mins < 60) return `Updated ${mins} min ago.`;
  const d = new Date(t);
  return `Updated at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`;
}

// ── Filters (drive the WHOLE page, E1–E3) ───────────────────────────────────

export const BRANDS = ['Binance', 'Binance.US', 'MEXC', 'Alpaca'];

/**
 * type: 'all' | 'live' | 'paper' | 'sim' (the mockup's segment; Simulated is
 * included by default because the onboarding demo account IS simulated).
 * brands: { [label]: false } hides that brand; unknown brands stay visible.
 * query: account-name search.
 */
export const DEFAULT_FILTERS = Object.freeze({ type: 'all', brands: {}, query: '' });

export function filtersActive(f = DEFAULT_FILTERS) {
  return f.type !== 'all' || Object.values(f.brands || {}).some((v) => v === false) || Boolean((f.query || '').trim());
}

export function filterExchanges(all = [], f = DEFAULT_FILTERS) {
  const q = (f.query || '').trim().toLowerCase();
  return all.filter((e) => {
    if (f.type && f.type !== 'all' && accountKind(e) !== f.type) return false;
    if (f.brands && f.brands[e.label] === false) return false;
    if (q && !String(e.name || '').toLowerCase().includes(q)) return false;
    return true;
  });
}

// ── Holdings / cash ─────────────────────────────────────────────────────────

// The backend prices these at $1 (portfolioValuation.js STABLES). DECISIONS
// #11: Cash = USD + USDT/USDC + USD-quote balances.
export const CASH_SYMBOLS = new Set(['USD', 'USDT', 'USDC', 'BUSD', 'TUSD', 'FDUSD', 'DAI']);
export const isCash = (h) => Boolean(h) && (h.assetClass === 'cash' || CASH_SYMBOLS.has(String(h.symbol || '').toUpperCase()));

export const DUST_USD = 0.005; // anything that would render as $0.00
export const SMALL_PCT = 1; // < 1 % of the in-scope total collapses (H3)

/**
 * Same asset on several accounts → one row with per-account `sources` (H2),
 * dust hidden (H4), sorted by value, split into main + small balances (H3).
 */
export function mergeHoldings(inScope = []) {
  const map = new Map();
  for (const e of inScope) {
    for (const h of e.holdings || []) {
      let agg = map.get(h.symbol);
      if (!agg) {
        agg = { symbol: h.symbol, assetClass: h.assetClass, price: h.price, change24Pct: h.change24Pct, qty: 0, value: 0, sources: [] };
        map.set(h.symbol, agg);
      }
      agg.qty += Number(h.qty) || 0;
      agg.value += Number(h.value) || 0;
      if ((!agg.price || agg.price <= 0) && h.price > 0) agg.price = h.price;
      agg.sources.push({ exId: e.id, exName: e.name, qty: Number(h.qty) || 0, value: Number(h.value) || 0 });
    }
  }
  const holdings = [...map.values()]
    .filter((h) => h.value >= DUST_USD)
    .map((h) => ({
      ...h,
      price: h.qty > 0 ? h.value / h.qty : h.price, // blended unit price
      sources: h.sources.sort((a, b) => b.value - a.value),
    }))
    .sort((a, b) => b.value - a.value);
  const total = holdings.reduce((s, h) => s + h.value, 0);
  let main = holdings.filter((h) => total > 0 && (h.value / total) * 100 >= SMALL_PCT);
  let small = holdings.filter((h) => !(total > 0 && (h.value / total) * 100 >= SMALL_PCT));
  // Never collapse everything.
  if (main.length === 0) { main = holdings; small = []; }
  return { holdings, main, small, smallTotal: small.reduce((s, h) => s + h.value, 0), total };
}

// One account's own holdings list: on a real exchange (live / paper) leftovers
// under $1 — what Binance itself calls dust — are hidden until asked for.
// A simulated account's assets were typed in by the user, so all are shown.
export const ACCOUNT_DUST_USD = 1;
export function accountHoldingRows(ex) {
  const rows = [...((ex && ex.holdings) || [])].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
  const total = rows.reduce((s, h) => s + (Number(h.value) || 0), 0);
  if (!ex || ex.simulated) return { rows, dust: [], dustTotal: 0, total };
  const isDust = (h) => !((Number(h.value) || 0) >= ACCOUNT_DUST_USD);
  const dust = rows.filter(isDust);
  return { rows: rows.filter((h) => !isDust(h)), dust, dustTotal: dust.reduce((s, h) => s + (Number(h.value) || 0), 0), total };
}

/** A live Binance.com account (not .US, not simulated) may hold a USDⓈ-M futures wallet. */
export function hasFuturesWallet(ex) {
  if (!ex || ex.simulated || accountKind(ex) !== 'live') return false;
  const name = String(ex.exchange || '').toLowerCase().replace(/\s+/g, '');
  return name.includes('binance') && name !== 'binance.us' && name !== 'binanceus';
}

/**
 * POST /api/futures/account → numbers + positions for the futures panel.
 * Positions sorted by |notional|; `side` from the sign (one-way) or positionSide (hedge).
 */
export function futuresWallet(data) {
  if (!data) return null;
  const n = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  const positions = (data.positions || []).map((p) => {
    const amt = n(p.positionAmt) || 0;
    const ps = String(p.positionSide || '').toUpperCase();
    return {
      key: `${p.symbol}-${ps || 'BOTH'}`,
      symbol: p.symbol,
      side: ps === 'LONG' || ps === 'SHORT' ? ps.toLowerCase() : amt < 0 ? 'short' : 'long',
      qty: Math.abs(amt),
      entry: n(p.entryPrice),
      mark: n(p.markPrice),
      pnl: n(p.unrealizedProfit),
      liq: n(p.liquidationPrice) || null, // Binance sends 0 when there is none
      leverage: n(p.leverage),
      marginType: p.marginType || null,
      notional: n(p.notional) != null ? Math.abs(n(p.notional)) : null,
    };
  }).sort((a, b) => (b.notional || 0) - (a.notional || 0));
  // Per-asset rows (newer api): { USDT: { wallet, available }, … }.
  const assets = {};
  (data.assets || []).forEach((a) => {
    const k = String(a.asset || '').toUpperCase();
    if (k) assets[k] = { wallet: n(a.walletBalance), available: n(a.availableBalance) };
  });
  return {
    equity: n(data.totalMarginBalance), // wallet + unrealized
    wallet: n(data.totalWalletBalance),
    available: n(data.availableBalance),
    unrealized: n(data.totalUnrealizedProfit),
    multiAssets: data.multiAssetsMargin == null ? null : Boolean(data.multiAssetsMargin),
    assets: data.assets ? assets : null,
    positions,
  };
}

/**
 * The page model. `model` is GET /api/portfolio/summary.
 * Returns null until the summary has loaded.
 */
export function buildView(model, { scope = null, filters = DEFAULT_FILTERS } = {}) {
  if (!model) return null;
  const all = model.exchanges || [];
  const exchanges = filterExchanges(all, filters);
  // If the scoped account got filtered out, fall back to All.
  const effScope = scope != null && exchanges.some((e) => e.id === scope) ? scope : null;
  const inScope = effScope == null ? exchanges : exchanges.filter((e) => e.id === effScope);
  const sum = (key) => inScope.reduce((s, e) => s + (Number(e[key]) || 0), 0);
  const balance = sum('balance');
  const change24Usd = sum('change24Usd');
  const prev = balance - change24Usd;
  const change24Pct = prev !== 0 ? (change24Usd / prev) * 100 : null;
  const pnlParts = inScope.filter((e) => e.pnlUsd != null);
  const pnlUsd = pnlParts.length ? pnlParts.reduce((s, e) => s + Number(e.pnlUsd), 0) : null;
  const pnlBase = pnlParts.reduce((s, e) => s + (Number(e.balance) - Number(e.pnlUsd)), 0);
  const pnlPct = pnlUsd != null && pnlBase > 0 ? (pnlUsd / pnlBase) * 100 : null;
  const merged = mergeHoldings(inScope);
  const cashUsd = merged.holdings.filter(isCash).reduce((s, h) => s + h.value, 0);
  const scopedEx = effScope == null ? null : exchanges.find((e) => e.id === effScope) || null;
  return {
    all,
    totalExchanges: all.length,
    exchanges,
    scope: effScope,
    scopedEx,
    inScope,
    balance,
    change24Usd,
    change24Pct,
    pnlUsd,
    pnlPct,
    cashUsd,
    cashPct: balance > 0 ? (cashUsd / balance) * 100 : null,
    ...merged,
    noMatch: all.length > 0 && exchanges.length === 0,
  };
}

/**
 * Scope-chip overflow (P3): first `max` accounts + "+N more"; the scoped
 * account always stays visible even past the cap.
 */
export function visibleChips(exchanges = [], scope = null, expanded = false, max = 6) {
  const capped = expanded ? exchanges : exchanges.slice(0, max);
  const shown = scope != null && !capped.some((e) => e.id === scope)
    ? [...capped, exchanges.find((e) => e.id === scope)].filter(Boolean)
    : capped;
  const hidden = Math.max(0, exchanges.length - Math.min(max, exchanges.length));
  return { shown, hidden, toggle: hidden > 0 || expanded };
}

// ── Allocation (donut + legend, K3) ─────────────────────────────────────────

/** "36%" / "4.5%" / "<1%" of `total`. */
export function shareLabel(value, total) {
  if (!(total > 0)) return '—';
  const q = (value / total) * 100;
  if (q < 1) return '<1%';
  return `${q.toFixed(q >= 10 ? 0 : 1)}%`;
}

const CLASS_LABEL = { crypto: 'Crypto', stock: 'Stocks', stock_option: 'Options', cash: 'Cash' };

/**
 * Group the in-scope value for the allocation toggle.
 *   'asset'   one slice per symbol
 *   'class'   Crypto / Stocks / Options / Cash (stablecoins count as Cash)
 *   'account' one slice per in-scope account
 *   'bot'     one slice per bot by live equity (`botEquity` = {botId: $})
 *             plus "Not in a bot" for the rest. Approximate: bot equity and
 *             account balances are measured differently.
 * Returns [{ key, label, value }] sorted by value, zero slices dropped.
 */
export function groupAllocation(view, by = 'asset', { bots = [], botEquity = {} } = {}) {
  if (!view) return { items: [], total: 0 };
  let items = [];
  if (by === 'asset') {
    items = view.holdings.map((h) => ({ key: h.symbol, label: h.symbol, value: h.value }));
  } else if (by === 'class') {
    const m = new Map();
    view.holdings.forEach((h) => {
      const k = isCash(h) ? 'cash' : h.assetClass || 'other';
      m.set(k, (m.get(k) || 0) + h.value);
    });
    items = [...m.entries()].map(([k, v]) => ({ key: k, label: CLASS_LABEL[k] || k.charAt(0).toUpperCase() + k.slice(1), value: v }));
  } else if (by === 'account') {
    items = view.inScope.map((e) => ({ key: String(e.id), label: e.name, value: Number(e.balance) || 0 }));
  } else if (by === 'bot') {
    const scopeIds = new Set(view.inScope.map((e) => e.id));
    items = bots
      .filter((b) => view.scope == null || scopeIds.has(b.exchange_id))
      .map((b) => ({ key: `bot-${b.id}`, label: b.name || `Bot ${b.id}`, value: Number(botEquity[b.id]) || 0 }));
    const inBots = items.reduce((s, i) => s + i.value, 0);
    const rest = view.balance - inBots;
    if (rest > DUST_USD) items.push({ key: 'rest', label: 'Not in a bot', value: rest, rest: true });
  }
  items = items.filter((i) => i.value > DUST_USD).sort((a, b) => b.value - a.value);
  return { items, total: items.reduce((s, i) => s + i.value, 0) };
}

/** Top `n` slices + "Others (N)" (K3). */
export function topSlices(items = [], n = 5) {
  const top = items.slice(0, n);
  const rest = items.slice(n);
  if (rest.length) top.push({ key: 'others', label: `Others (${rest.length})`, value: rest.reduce((s, i) => s + i.value, 0), others: true });
  return top;
}

// ── Bots (socket stats) ─────────────────────────────────────────────────────

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * `botUpdate:<id>.stats` arrives from a Redis hash, so every value may be a
 * string (or the whole object a JSON string). Missing → null, never 0.
 */
export function parseStats(raw) {
  let s = raw;
  if (typeof s === 'string') {
    try { s = JSON.parse(s); } catch (_) { return null; }
  }
  if (!s || typeof s !== 'object' || Object.keys(s).length === 0) return null;
  return {
    realizedPnL: numOrNull(s.realizedPnL),
    equity: numOrNull(s.equity),
    initialCapital: numOrNull(s.initialCapital),
    positionQty: numOrNull(s.positionQty),
    entryPrice: numOrNull(s.entryPrice),
    markPrice: numOrNull(s.markPrice),
    shortPositionQty: numOrNull(s.shortPositionQty),
    shortEntryPrice: numOrNull(s.shortEntryPrice),
  };
}

/** Open positions of one bot: [{ side: 'long'|'short', qty, entry, mark, value, pnl }]. */
export function botPositions(stats) {
  if (!stats) return [];
  const out = [];
  const mark = stats.markPrice;
  if (stats.positionQty > 0) {
    const qty = stats.positionQty;
    out.push({
      side: 'long',
      qty,
      entry: stats.entryPrice,
      mark,
      value: mark != null ? qty * mark : null,
      pnl: mark != null && stats.entryPrice != null ? (mark - stats.entryPrice) * qty : null,
    });
  }
  if (stats.shortPositionQty > 0) {
    const qty = stats.shortPositionQty;
    out.push({
      side: 'short',
      qty,
      entry: stats.shortEntryPrice,
      mark,
      value: mark != null ? qty * mark : null,
      pnl: mark != null && stats.shortEntryPrice != null ? (stats.shortEntryPrice - mark) * qty : null,
    });
  }
  return out;
}

/**
 * Total P&L of a bot = realized + the open positions' mark-to-market.
 * null when the bot has sent no stats (never a made-up 0).
 */
export function botPnl(stats) {
  if (!stats || stats.realizedPnL == null) return { realized: null, open: null, total: null };
  const pos = botPositions(stats);
  const openKnown = pos.every((p) => p.pnl != null);
  const open = pos.length ? (openKnown ? pos.reduce((s, p) => s + p.pnl, 0) : null) : 0;
  return { realized: stats.realizedPnL, open, total: open == null ? stats.realizedPnL : stats.realizedPnL + open };
}

export const parseTradeInfo = (bot) => {
  if (!bot || !bot.trade_info) return {};
  if (typeof bot.trade_info === 'object') return bot.trade_info;
  try { return JSON.parse(bot.trade_info) || {}; } catch (_) { return {}; }
};

/** 'stock' | 'crypto' for the Open positions tile sub line. */
export function botAssetKind(bot) {
  const ti = parseTradeInfo(bot);
  const ac = String(ti.asset_class || (bot && bot.asset_class) || '').toLowerCase();
  if (ac.startsWith('stock')) return 'stock';
  const ex = String((bot && bot.exchange && bot.exchange.exchange) || '').toLowerCase();
  return ex.includes('alpaca') ? 'stock' : 'crypto';
}

/** Running / Paused, from the socket's stop_trade_ASSET when it has arrived. */
export function botStatus(bot, live = {}) {
  const stopped = live.stop_trade_ASSET != null ? Boolean(live.stop_trade_ASSET) : Boolean(bot && bot.stop_trade_ASSET);
  // The engine's futures refusal (api futures_block, read live from
  // botStats:{id}) is trusted like the socket's error: it is engine state,
  // not the row's error_message.
  // The socket's word (live.futures_block, set or cleared) beats the row's.
  const block = live && 'futures_block' in live ? futuresBlockOf({ futures_block: live.futures_block }) : futuresBlockOf(bot);
  if (block) {
    const meta = getBotStatusMeta(bot && bot.status, { futuresBlock: block });
    if (meta.key === 'blocked') return { key: 'stop', label: meta.label, detail: meta.description };
  }
  // Only the socket's error is trusted: the row's error_message is often stale.
  if (live.error_message) return { key: 'warn', label: 'Needs attention', detail: live.error_message };
  return stopped ? { key: 'pause', label: 'Paused' } : { key: 'run', label: 'Running' };
}

/**
 * Bots that belong on the page: terminated bots (e.g. finished scanner
 * children) are left out, and when a scope or a type/brand/search filter is
 * active only bots on the accounts in view are kept.
 */
export function botsInView(bots = [], view = null, filtered = false) {
  const live = bots.filter((b) => String((b && b.status) || '').toLowerCase() !== 'terminated');
  if (!view || (view.scope == null && !filtered)) return live;
  const ids = new Set(view.inScope.map((e) => e.id));
  return live.filter((b) => ids.has(b.exchange_id));
}

/**
 * The Bots tab's grouping: bots the user made sit under "Your bots", the bots a
 * scanner started under one group per scanner ("Scanner · <name>"). Returns the
 * rows ordered own-first, then scanners by name, and the group label of each,
 * or `groupOf: null` when no scanner bot is listed (the plain list stays as is).
 * `profiles`: the scanner profiles (for names); a scanner not among them (deleted,
 * or not loaded yet) is named by its id.
 */
export const OWN_BOTS_GROUP = 'Your bots';
export function botGroups(bots = [], profiles = []) {
  const names = new Map((profiles || []).filter(Boolean).map((p) => [String(p.id), p.name]));
  const scannerOf = (b) => (b && b.spawned_from_profile_id != null ? String(b.spawned_from_profile_id) : null);
  const labelOf = (b) => {
    const id = scannerOf(b);
    if (id == null) return OWN_BOTS_GROUP;
    return `Scanner · ${names.get(id) || `#${id}`}`;
  };
  if (!bots.some((b) => scannerOf(b) != null)) return { rows: bots, groupOf: null };
  const own = bots.filter((b) => scannerOf(b) == null);
  const spawned = bots.filter((b) => scannerOf(b) != null)
    .map((b, i) => ({ b, i, l: labelOf(b) }))
    .sort((x, y) => x.l.localeCompare(y.l) || x.i - y.i)
    .map((e) => e.b);
  return { rows: [...own, ...spawned], groupOf: labelOf };
}

/** Every bot's open positions flattened for the Open positions tab + counts. */
export function openPositionRows(bots = [], statsById = {}) {
  const rows = [];
  bots.forEach((b) => {
    const ti = parseTradeInfo(b);
    botPositions(statsById[b.id]).forEach((p, i) => {
      rows.push({ key: `${b.id}-${p.side}-${i}`, botId: b.id, botName: b.name || `Bot ${b.id}`, symbol: ti.symbol || '—', kind: botAssetKind(b), ...p });
    });
  });
  const known = rows.every((r) => r.value != null);
  return {
    rows,
    count: rows.length,
    stocks: rows.filter((r) => r.kind === 'stock').length,
    crypto: rows.filter((r) => r.kind === 'crypto').length,
    worth: rows.length && known ? rows.reduce((s, r) => s + r.value, 0) : null,
  };
}

/**
 * Normalise a `botUpdate.history` array. Entries often arrive as raw JSON
 * strings (trade tradeExecutors.js), and `buy_sell_history` inside may be a
 * string too — parsed the way Trader_Form/botLive.js socketHistoryEntries
 * does. Anything that will not parse is dropped.
 * @returns [{ date, buy_sell_history, bot_id? }]
 */
export function parseSocketHistory(list) {
  if (!Array.isArray(list)) return [];
  return list.map((entry) => {
    let parsed = entry;
    if (typeof entry === 'string') {
      try { parsed = JSON.parse(entry); } catch (_) { return null; }
    }
    if (!parsed || typeof parsed !== 'object') return null;
    let text = parsed.buy_sell_history;
    if (typeof text === 'string') {
      try { text = JSON.parse(text); } catch (_) { /* keep the plain string */ }
    }
    return { date: parsed.date, buy_sell_history: text, bot_id: parsed.bot_id };
  }).filter(Boolean);
}

/**
 * Recent activity from the history entries the socket `join` sends per bot
 * (and, on demand, /api/userhistory/all_by_user). Newest first.
 */
export function activityRows(historyByBot = {}, botsById = {}, limit = 25) {
  const rows = [];
  Object.entries(historyByBot).forEach(([botId, list]) => {
    (list || []).forEach((e) => {
      let t = e.text != null ? e.text : e.buy_sell_history;
      if (typeof t === 'string') {
        try { t = JSON.parse(t); } catch (_) { /* keep the string */ }
      }
      const ms = new Date(e.date || e.ts || e._ts).getTime();
      const bot = botsById[botId];
      const obj = t && typeof t === 'object' ? t : null;
      rows.push({
        key: `${botId}|${Number.isFinite(ms) ? ms : e.date}|${obj ? `${obj.side}|${obj.price}|${obj.quantity}` : String(t)}`,
        ts: Number.isFinite(ms) ? ms : 0,
        botId: Number(botId),
        botName: (bot && bot.name) || (e.trading_bot && e.trading_bot.name) || `Bot ${botId}`,
        side: obj && obj.side ? String(obj.side).toLowerCase() : null,
        type: obj && obj.type ? String(obj.type) : null,
        symbol: (obj && obj.symbol) || null,
        qty: obj ? numOrNull(obj.quantity) : null,
        price: obj ? numOrNull(obj.price) : null,
        text: obj ? null : String(t == null ? '' : t),
      });
    });
  });
  const seen = new Set();
  return rows
    .filter((r) => (seen.has(r.key) ? false : seen.add(r.key)))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, limit);
}

// ── Trading controls meters ─────────────────────────────────────────────────

/**
 * The three limit meters on the Trading controls bar.
 * Daily loss is $ / $ (dailyMaxLoss and snapshot.dayPnL are both dollars);
 * the mockup's "0.4% / 2%" has no data behind it.
 */
export function riskMeters(risk, exposure) {
  const limits = (risk && risk.limits) || {};
  const snap = (risk && risk.snapshot) || null;
  const dayPnL = snap ? numOrNull(snap.dayPnL) : null;
  const loss = dayPnL == null ? null : Math.max(0, -dayPnL);
  const lossMax = numOrNull(limits.dailyMaxLoss);
  const open = snap ? numOrNull(snap.openPositions) : null;
  const openMax = numOrNull(limits.maxOpenPositions);
  const h = exposure && exposure.holdings && exposure.holdings.available ? exposure.holdings : null;
  const beta = h ? numOrNull(h.marketBeta) : null;
  const betaMax = numOrNull(limits.maxMarketBeta);
  const money0 = (v) => `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return [
    {
      key: 'loss',
      label: 'Daily loss',
      value: loss,
      max: lossMax,
      text: `${loss == null ? '—' : money0(loss)} / ${lossMax == null ? 'no limit' : money0(lossMax)}`,
      title: dayPnL == null ? 'No P&L snapshot yet today.' : `Today's P&L ${dayPnL < 0 ? '−' : '+'}${money0(Math.abs(dayPnL))}: change in bot equity since 00:00 UTC, realized and unrealized.`,
    },
    {
      key: 'open',
      label: 'Open positions',
      value: open,
      max: openMax,
      text: `${open == null ? '—' : open} / ${openMax == null ? 'no limit' : openMax}`,
    },
    {
      key: 'beta',
      label: 'Market beta',
      value: beta == null ? null : Math.abs(beta),
      max: betaMax,
      text: `${beta == null ? '—' : beta.toFixed(2)} / ${betaMax == null ? 'no limit' : betaMax}`,
      title: h ? undefined : 'Market beta is measured on stock holdings only.',
    },
  ];
}

/** Halt status for the bar: { key, label, note }. */
export function haltStatus(halted = {}, fmtWhen = (v) => (v ? new Date(v).toLocaleString() : null)) {
  const isHalted = Boolean(halted.global || halted.user);
  const unknown = Boolean(halted.degraded) && !isHalted;
  const label = halted.global ? 'HALTED: PLATFORM WIDE' : halted.user ? 'TRADING HALTED' : unknown ? 'HALT STATE UNKNOWN' : 'TRADING ALLOWED';
  let note = null;
  if (unknown) {
    note = `The halt flags could not be read${halted.error ? ` (${halted.error})` : ''}. The trading engine still enforces whatever is set; retry in a moment.`;
  } else if (isHalted) {
    note = [
      halted.reason ? `“${halted.reason}”` : 'No reason recorded',
      fmtWhen(halted.since) ? `since ${fmtWhen(halted.since)}` : null,
      halted.by ? `by ${halted.by}` : null,
      halted.global ? 'only an administrator can lift a platform-wide halt' : null,
    ].filter(Boolean).join(' · ');
  }
  return { isHalted, unknown, label, tone: isHalted || unknown ? 'watch' : 'up', note };
}

/** limits vs effective disagree → the engine is not using the saved limits (R7). */
export function mirrorMismatch(risk, fields) {
  if (!risk) return false;
  const row = risk.limits || {};
  const live = risk.effective || {};
  return fields.some((f) => Number(row[f.key] ?? -1) !== Number(live[f.key] ?? -1));
}

// ── Risk exposure ───────────────────────────────────────────────────────────

/** Plain factor names (mockup) for the FF-6 keys the api returns. */
export const FACTOR_PLAIN = {
  'Mkt-RF': 'Market',
  SMB: 'Small companies',
  HML: 'Value',
  RMW: 'Profitable companies',
  CMA: 'Conservative investors',
  Mom: 'Momentum',
};
export const factorName = (f) => FACTOR_PLAIN[f] || f;

/**
 * The card's figures. The api computes the factor share and the top sector
 * (services/liveFactorRisk.js `factorShareOfVar`, `topSector`); they are used
 * as sent. Formulas still needing the user's OK (audit A §2.3):
 *   invested = holdings.grossLeverage (gross stock exposure / equity)
 *   whatIf   = marketBeta × 10: the expected % fall of the stocks for a 10 %
 *              market fall (the beta is measured on the stock book)
 */
export function exposureSummary(data) {
  const h = data && data.holdings;
  if (!h || !h.available) return null;
  const beta = numOrNull(h.marketBeta);
  const top = h.topSector && h.topSector.sector ? h.topSector : null;
  return {
    beta,
    invested: numOrNull(h.grossLeverage),
    riskFromFactors: numOrNull(h.factorShareOfVar),
    whatIf: beta == null ? null : beta * 10,
    topSector: top ? { sector: top.sector, grossWeight: numOrNull(top.grossWeight) } : null,
    coverage: numOrNull(h.coverage),
    covered: h.covered,
    positions: h.positions,
  };
}

/** Centre-anchored bar geometry: fill left/width % and the limit tick (positive side, as drawn in the mockup). */
export function betaBar(value, limit) {
  const v = Number(value);
  const w = Number.isFinite(v) ? Math.min(Math.abs(v) * 45, 48) : 0;
  const left = Number.isFinite(v) && v < 0 ? 50 - w : 50;
  const lim = Number(limit);
  const ticks = Number.isFinite(lim) && lim > 0
    ? [Math.min(98, 50 + Math.min(lim * 45, 48))]
    : [];
  return { left, width: w, negative: Number.isFinite(v) && v < 0, ticks };
}

// ── Value chart ─────────────────────────────────────────────────────────────

export const RANGE_MS = { '1D': 864e5, '1W': 7 * 864e5, '1M': 30 * 864e5, '1Y': 365 * 864e5 };
export const BTC_INTERVAL = { '1D': '1h', '1W': '1h', '1M': '1d', '1Y': '1d' };

/**
 * Rebase BTC closes onto the portfolio's first value BY TIME: for each
 * snapshot, the latest bar at or before it (bars and snapshots have
 * different cadences). Returns numbers aligned with `series`, null where no
 * bar precedes the point.
 */
export function rebaseBench(series = [], bars = []) {
  if (series.length < 2 || !bars.length) return null;
  const sorted = [...bars].map((b) => ({ t: Number(b.t), c: Number(b.c) })).filter((b) => Number.isFinite(b.t) && b.c > 0).sort((a, b) => a.t - b.t);
  if (!sorted.length) return null;
  let j = -1;
  const closes = series.map((p) => {
    const t = Number(new Date(p.t).getTime());
    while (j + 1 < sorted.length && sorted[j + 1].t <= t) j += 1;
    return j >= 0 ? sorted[j].c : null;
  });
  const base = closes.find((c) => c != null);
  const v0 = Number(series[0].v);
  if (base == null || !(v0 > 0)) return null;
  const out = closes.map((c) => (c == null ? null : (v0 * c) / base));
  return out.filter((x) => x != null).length >= 2 ? out : null;
}

/** SVG paths for the value chart (straight segments, like the mockup). */
export function chartPaths(values = [], bench = null, w = 700, h = 206, top = 10) {
  const pts = values.map(Number);
  const all = [...pts, ...(bench || []).filter((x) => x != null)];
  if (pts.length < 2) return null;
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const span = hi - lo || 1;
  const x = (i) => (i / (pts.length - 1)) * w;
  const y = (v) => (hi === lo ? (top + h) / 2 : top + ((hi - v) / span) * (h - top));
  const line = pts.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(pts.length - 1).toFixed(1)} ${h} L0 ${h} Z`;
  let benchLine = null;
  if (bench) {
    let started = false;
    benchLine = bench.map((v, i) => {
      if (v == null) return '';
      const seg = `${started ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`;
      started = true;
      return seg;
    }).filter(Boolean).join(' ');
  }
  return { line, area, bench: benchLine, lo, hi };
}

/** Period change of a value series in %, or null. */
export function periodChange(values = []) {
  if (values.length < 2) return null;
  const a = Number(values[0]);
  const b = Number(values[values.length - 1]);
  return a > 0 ? ((b - a) / a) * 100 : null;
}

// ── Capital allocation ──────────────────────────────────────────────────────

/** Actual vs target in percentage points; `null` when equity is unknown. */
export function allocationDrift(row) {
  const act = numOrNull(row && row.equitySharePct);
  const tgt = numOrNull(row && row.targetPct);
  if (act == null) return { actual: null, target: tgt, diff: null, off: false };
  const diff = tgt == null ? null : act - tgt;
  return { actual: act, target: tgt, diff, off: diff != null && Math.abs(diff) >= 3 };
}
